import type { RuntimeConfig } from "nuxt/schema";
import type {
  BrowserAuthError,
  PopupRequest,
  Configuration,
  IPublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  EventMessage,
} from "@azure/msal-browser";
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  EventType,
  BrowserCacheLocation,
  BrowserAuthErrorCodes,
} from "@azure/msal-browser";
import type { AuthEventMessage, AuthFlow, AuthLoginOptions } from "../types";
import BaseAuthClass from "../utils/base-auth-class";

export default class MsalAuthClass extends BaseAuthClass {
  #msalConfig: Configuration;
  #msalInstance: IPublicClientApplication;
  #msalAccount: AccountInfo | undefined;
  #msalToken: AuthenticationResult | undefined;
  #tokenPromise: Promise<AuthenticationResult> | undefined;

  #authFlow: AuthFlow;
  #getAPIScopes(): string[] {
    return String(this.config.public.AZURE_AD_SCOPES) === ""
      ? [`api://${this.config.public.AZURE_AD_CLIENT_ID}/ListPlants`]
      : String(this.config.public.AZURE_AD_SCOPES).split(",");
  }

  #getMsalConfig(): Configuration {
    const auth: Configuration["auth"] = this.isMultiTenant
      ? {
          clientId: this.config.public.AZURE_AD_CLIENT_ID,
          authority: this.config.public.AZURE_AD_AUTHORITY,
          postLogoutRedirectUri: this.config.public.REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
          knownAuthorities: String(this.config.public.AZURE_AD_KNOWN_AUTHORITIES).split(","),
          redirectUri: "/", // Must be registered as a SPA redirectURI on your app registration
        }
      : {
          clientId: this.config.public.AZURE_AD_CLIENT_ID,
          authority: "https://login.microsoftonline.com/" + this.config.public.AZURE_AD_TENANT_ID,
          protocolMode: "AAD",
          redirectUri: "/", // Must be registered as a SPA redirectURI on your app registration
          postLogoutRedirectUri: this.config.public.REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
          //mainWindowRedirectUri: this.config.public.redirectUri, // Must be registered as a SPA redirectURI on your app registration
        };

    return {
      auth,
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage, // This configures where your cache will be stored
        claimsBasedCachingEnabled: true, // Maintains v2 behavior
        //storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
      },
    };
  }

  constructor(config: RuntimeConfig) {
    super(config);
    this.#msalConfig = this.#getMsalConfig();
    this.#msalInstance = new PublicClientApplication(this.#msalConfig);
    this.#msalInstance.addEventCallback((e) => this.#handleMsalEvent(e));
    this.#authFlow = "idle";
    this.#msalAccount = undefined;
    this.#tokenPromise = undefined;
    this.#msalToken = undefined;
  }

  get authFlow() {
    return this.#authFlow;
  }

  get authValidUntil() {
    return this.#msalToken?.expiresOn?.getTime() ?? undefined;
  }

  async resetAuthFlow() {
    const accounts = this.#msalInstance.getAllAccounts();
    this.#authFlow = accounts.length === 0 ? "idle" : "authenticated";
  }

  async initialize() {
    await this.#msalInstance.initialize();
    await this.#msalInstance.handleRedirectPromise();
    const accounts = this.#msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      await this.getToken();
    }
  }

  async login(options?: AuthLoginOptions) {
    this.#msalToken = await this.#msalInstance
      .loginPopup({
        redirectUri: this.#msalConfig.auth.redirectUri || "/",
        scopes: this.#getAPIScopes(),
        ...(options ?? {}),
      })
      .catch(async (err: BrowserAuthError) => {
        if (err.errorCode === BrowserAuthErrorCodes.popupWindowError) {
          await this.#msalInstance.loginRedirect({
            redirectUri: this.#msalConfig.auth.redirectUri || "/",
            scopes: this.#getAPIScopes(),
            ...(options ?? {}),
          });
          return undefined;
        }

        if (err.errorCode === BrowserAuthErrorCodes.userCancelled) {
          // User cancelled the login, we can just return undefined
          return;
        }

        // Since the $ck.utils.UserFriendlyError is hiding the real error, show this in the dev console
        devConsole.warn(`🔒 ckAuth: Error during login: ${err.errorCode} - ${err.errorMessage} - ${err.subError}`);

        throw new $ck.utils.UserFriendlyError("auth.signInUserNotExistsErrorMessage");
      });
    this.#msalAccount = this.#msalToken?.account;

    if (this.#msalAccount) {
      this.#msalInstance.setActiveAccount(this.#msalAccount);
    }
    return !!this.#msalAccount;
  }

  async logout() {
    await this.#msalInstance.handleRedirectPromise();

    await this.#msalInstance
      .logoutPopup({
        postLogoutRedirectUri: "/",
      })
      .catch((err) => {
        if (err.errorCode === BrowserAuthErrorCodes.popupWindowError) {
          this.#msalInstance.logoutRedirect({
            postLogoutRedirectUri: "/",
          });
        }
        throw err;
      });
    await this.resetAuthFlow();
    return this.#authFlow === "idle";
  }

  async refreshToken() {
    await this.#silentlyGetToken();
    return this.#msalToken?.accessToken ?? "";
  }

  async #silentlyGetToken() {
    const tokenConfig = {
      scopes: this.#getAPIScopes(),
    };
    try {
      this.#tokenPromise = this.#msalInstance.acquireTokenSilent(tokenConfig);
      this.#msalToken = await this.#tokenPromise;
      this.#tokenPromise = undefined;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await this.#msalInstance.acquireTokenRedirect(tokenConfig);
      } else {
        throw e;
      }
    }
  }

  async getToken() {
    if (this.#tokenPromise) await this.#tokenPromise;
    if (this.isExpired()) {
      // this will either refresh the token silently or force a redirect through the login flow.
      await this.#silentlyGetToken();
    }

    return this.#msalToken?.accessToken ?? "";
  }

  async getAccounts() {
    return this.#msalInstance.getAllAccounts().map((el) => {
      return {
        username: el.username,
        name: el.name ?? "",
        id: el.idToken ?? "",
      };
    });
  }

  async isAuthed() {
    if (this.#tokenPromise) await this.#tokenPromise;
    return !this.isExpired();
  }

  async clearData() {
    await this.#msalInstance?.clearCache();
    await this.resetAuthFlow();
  }

  async handleAuthError(error: Error): Promise<boolean> {
    devConsole.warn("🔒 ckAuth: msal handleAuthError", error);
    return true;
  }

  #castAccount(account?: AccountInfo | null) {
    return {
      username: account?.username ?? "",
      name: account?.name ?? "",
      id: account?.idToken ?? "",
    };
  }

  #castToken(token?: AuthenticationResult | null) {
    return {
      token: token?.accessToken ?? "",
      expiresOn: token?.expiresOn ?? new Date(),
    };
  }

  #triggerBusyEvent(busy: boolean, trigger?: "token-refresh" | "login" | "logout") {
    this.broadcastEvent({
      type: "auth-busy-state-change",
      detail: {
        busy,
        trigger,
      },
    });
  }

  #handleMsalEvent(event: EventMessage) {
    switch (event.eventType) {
      case EventType.INITIALIZE_START:
        this.#triggerBusyEvent(true);
        break;
      case EventType.INITIALIZE_END:
        this.#triggerBusyEvent(false);
        break;
      case EventType.SSO_SILENT_START:
      case EventType.HANDLE_REDIRECT_START:
      case EventType.LOGIN_START:
        this.#triggerBusyEvent(true, "login");
        break;
      case EventType.LOGIN_SUCCESS: {
        this.#triggerBusyEvent(false, "login");
        const payload = event.payload as PopupRequest;
        const account = payload.account!;
        this.#msalAccount = account;
        this.#msalInstance.setActiveAccount(account);
        this.#authFlow = "post-login";
        this.broadcastEvent({
          type: "auth-login-success",
          detail: {
            account: this.#castAccount(account),
            token: this.#castToken(this.#msalToken),
          },
        });
        break;
      }
      case EventType.LOGIN_FAILURE:
      case EventType.SSO_SILENT_SUCCESS:
      case EventType.SSO_SILENT_FAILURE:
      case EventType.HANDLE_REDIRECT_END:
        this.#triggerBusyEvent(false, "login");
        break;
      case EventType.ACQUIRE_TOKEN_START:
      case EventType.ACQUIRE_TOKEN_NETWORK_START:
      case EventType.ACQUIRE_TOKEN_BY_CODE_START:
        this.#triggerBusyEvent(true, "token-refresh");
        break;
      case EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS: {
        this.#triggerBusyEvent(false, "token-refresh");

        const message: AuthEventMessage = {
          type: "auth-token-refresh-success",
          detail: {
            account: this.#castAccount(this.#msalAccount),
            token: this.#castToken(this.#msalToken),
          },
        };
        this.broadcastEvent(message);
        break;
      }
      case EventType.ACQUIRE_TOKEN_FAILURE: {
        this.#triggerBusyEvent(false, "token-refresh");
        const errorPayload = event.error;
        this.broadcastEvent({
          type: "auth-token-refresh-failed",
          detail: errorPayload ?? new Error("Token refresh failed"),
        });
        break;
      }
      case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
        this.#triggerBusyEvent(false, "token-refresh");
        break;
      case EventType.LOGOUT_START: //msal:logoutStart",
        this.#triggerBusyEvent(true, "logout");
        break;
      case EventType.LOGOUT_END:
        this.#triggerBusyEvent(false, "logout");
        break;
      case EventType.LOGOUT_SUCCESS:
        this.broadcastEvent({
          type: "auth-logout",
        });
        break;
    }
  }
}
